import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Card, Button, Space, Tag, Table, Spin, Alert, Descriptions, Popconfirm, message } from 'antd';
import { EditOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../api';
import {
  useProjectOptions, LinksView, MembersView, ProjectComments, TaskDrawer, taskColumns, taskDeleteColumn,
  ProjectEditModal, AddTaskModal, MassAddTaskModal,
} from '../components/ProjectParts';
import { useBackFallback, usePageLabel } from '../components/BackChip';

// A Project's own page (/folders/:id/projects/:projectId): where the work
// is -- its details, tasks and comments. In the code and API a Project is a
// "folder" (ProjectFolders) inside a "project" (Projects, shown as a Folder);
// see ProjectParts. Loaded through its Folder, so it shows exactly what the
// Folder page would of it, with the same access.

export default function ProjectPage() {
  const { id, projectId } = useParams();
  const pid = Number(id);            // the Folder (code: project)
  const fid = Number(projectId);     // this Project (code: folder)
  const navigate = useNavigate();
  const options = useProjectOptions();
  const [p, setP] = useState(null);
  // Names this page in Recent and on the next page's Back chip.
  const projName = p?.Folders?.find((x) => x.ID === fid)?.Name;
  usePageLabel(projName ? `Project: ${projName} · ${p.Name}` : null);
  // The Back chip (Layout): where you came from, else this Folder.
  useBackFallback(`/folders/${pid}`, p?.Name || 'Folder');
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [massAdding, setMassAdding] = useState(false);
  const [taskId, setTaskId] = useState(null);
  // Reloads the comments when the task panel closes (a task comment may
  // have been added there).
  const [commentsKey, setCommentsKey] = useState(0);
  // ?task=<id> (the "assigned you a task" alert): open that task's panel.
  const [searchParams] = useSearchParams();
  const linkTask = Number(searchParams.get('task')) || null;
  useEffect(() => { if (linkTask) setTaskId(linkTask); }, [linkTask]);

  const load = useCallback(() => {
    api.get(`/api/projects/${pid}`)
      .then((r) => {
        setP(r.data);
        setError(r.data.Folders.some((x) => x.ID === fid) ? null : 'Project not found.');
      })
      .catch((err) => setError(err.response?.status === 404 ? 'Project not found.' : 'Failed to load the project.'));
  }, [pid, fid]);
  useEffect(() => { load(); }, [load]);

  if (error) return <Alert type="error" showIcon message={error} />;
  if (!p) return <Spin />;
  const f = p.Folders.find((x) => x.ID === fid);
  // Campaigns: the project's own, else its folder's (the task scope).
  const ownCamps = f.Campaigns || [];
  const campaigns = ownCamps.length ? ownCamps : p.Campaigns;
  const open = (f.Tasks || []).filter((t) => t.Status !== 'Done').length;

  return (
    <div>
      <Space align="center" style={{ marginBottom: 4 }} wrap>
        <Typography.Title level={3} style={{ margin: 0 }}>{f.Name}</Typography.Title>
        {f.TypeName && <Tag color="geekblue">{f.TypeName}</Tag>}
        {f.IsArchived && <Tag>Archived</Tag>}
      </Space>
      <div style={{ marginBottom: 12, marginLeft: 40 }}>
        <Typography.Text type="secondary">Folder: </Typography.Text>
        <Link to={`/folders/${pid}`}>{p.Name}</Link>
      </div>

      <Card size="small" title="Project" style={{ marginBottom: 16 }}
        extra={p.CanManage && (
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={() => setEditing(true)}>Edit</Button>
            {/* A project with no tasks can be deleted. */}
            {!(f.Tasks || []).length && (
                <Popconfirm title="Delete this project?" description="It has no tasks." okText="Delete"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => api.delete(`/api/project-folders/${fid}`)
                    .then(() => { message.success('Project deleted.'); navigate(`/folders/${pid}`); })
                    .catch((err) => message.error(err.response?.data?.error || 'Delete failed.'))}>
                  <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
                </Popconfirm>
              )}
          </Space>
        )}>
        <Descriptions size="small" bordered column={{ xs: 1, md: 2 }}>
          <Descriptions.Item label="Dates" span={2}>
            {f.StartDate || f.EndDate ? `${f.StartDate || '…'} – ${f.EndDate || '…'}` : '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Campaigns" span={2}>
            {campaigns.length ? campaigns.map((c) => <Tag key={c.ID}>{c.Name}</Tag>) : '—'}
            {campaigns.length > 0 && !ownCamps.length && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>(the folder&apos;s)</Typography.Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Description" span={2}>
            {f.Description ? <div style={{ whiteSpace: 'pre-wrap' }}>{f.Description}</div> : '—'}
          </Descriptions.Item>
          {p.CanManage && (
            <Descriptions.Item label="Project members" span={2}><MembersView members={f.Members} /></Descriptions.Item>
          )}
          <Descriptions.Item label="Links" span={2}>{f.Links.length ? <LinksView links={f.Links} /> : '—'}</Descriptions.Item>
        </Descriptions>
      </Card>

      <Card size="small" style={{ marginBottom: 16 }}
        title={<Space>Tasks<Typography.Text type="secondary" style={{ fontWeight: 'normal' }}>({open} open)</Typography.Text></Space>}
        extra={f.CanAddTasks && (
          <Space>
            <Button size="small" onClick={() => setMassAdding(true)}>Mass Add Tasks</Button>
            <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>Add Task</Button>
          </Space>
        )}>
        <Table rowKey="ID" size="small" pagination={false} dataSource={f.Tasks} columns={p.CanManage ? [taskDeleteColumn(() => true, load), ...taskColumns()] : taskColumns()}
          scroll={{ x: 'max-content' }} locale={{ emptyText: 'No tasks.' }}
          onRow={(t) => ({ onClick: () => setTaskId(t.ID), style: { cursor: 'pointer' } })} />
      </Card>

      {f.Sees && (
        <Card size="small" title="Comments">
          <ProjectComments key={commentsKey} folderId={fid} withTasks onOpenTask={setTaskId} />
        </Card>
      )}

      <TaskDrawer open={!!taskId} taskId={taskId} onClose={() => { setTaskId(null); setCommentsKey((k) => k + 1); }}
        onChanged={load} options={options} />
      <ProjectEditModal open={editing} folderId={pid} project={f} options={options}
        onClose={() => setEditing(false)} onSaved={load} />
      <AddTaskModal open={adding} folderId={pid} projectId={fid} campaignIds={campaigns.map((c) => c.ID)}
        options={options} onClose={() => setAdding(false)} onSaved={load} />
      <MassAddTaskModal open={massAdding} folderId={pid} projectId={fid} options={options}
        onClose={() => setMassAdding(false)} onSaved={load} />
    </div>
  );
}
